import React, { useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Alert, AlertTitle, AlertDescription } from '../../../components/ui/alert';
import { Download, Mail, Copy, CheckCircle2, Share2, FileText, X, ArrowRight } from 'lucide-react';
import * as XLSX from 'xlsx';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const exportToExcel = (data) => {
  const wb = XLSX.utils.book_new();
  
  // Summary Sheet
  const summaryData = [
    ['DeltaOps Cost Analysis Summary'],
    [''],
    ['Current Situation'],
    ['Engineers', data.engineerCount],
    ['Monthly Revenue', data.monthlyRevenue],
    ['Resolution Time (hours)', data.currentResolutionTime],
    [''],
    ['Potential Impact'],
    ['Annual Savings', data.annualSavings],
    ['Time Reduction', `${data.timeReduction}%`],
    ['New Resolution Time (hours)', data.improvedResolutionTime.toFixed(1)],
    [''],
    ['Benefits Include'],
    ['• Engineering time cost savings'],
    ['• Revenue impact reduction'],
    ['• SLA/customer satisfaction improvements']
  ];

  const ws = XLSX.utils.aoa_to_sheet(summaryData);

  // Styling for summary sheet
  ws['!cols'] = [{ width: 35 }, { width: 15 }];
  ws['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 4 } },
    { s: { r: 2, c: 0 }, e: { r: 2, c: 4 } },
    { s: { r: 7, c: 0 }, e: { r: 7, c: 4 } },
    { s: { r: 12, c: 0 }, e: { r: 12, c: 4 } }
  ];

  // Format headers
  ['A1', 'A3', 'A8', 'A13'].forEach(cell => {
    if (ws[cell]) {
      ws[cell].s = {
        font: { bold: true, sz: 14 },
        alignment: { horizontal: 'left' }
      };
    }
  });

  // Format numbers
  ['B4', 'B5'].forEach(cell => {
    if (ws[cell]) {
      ws[cell].z = cell === 'B5' ? '$#,##0' : '#,##0';
    }
  });

  // Add borders
  for (let R = 0; R < summaryData.length; R++) {
    for (let C = 0; C < 2; C++) {
      const cell_ref = XLSX.utils.encode_cell({ r: R, c: C });
      if (ws[cell_ref]) {
        ws[cell_ref].s = {
          ...ws[cell_ref].s,
          border: {
            top: { style: 'thin' },
            bottom: { style: 'thin' },
            left: { style: 'thin' },
            right: { style: 'thin' }
          }
        };
      }
    }
  }

  // Add summary sheet
  XLSX.utils.book_append_sheet(wb, ws, 'Summary');

  // Chart Data Sheet
  const chartData = [
    ['Scenario', 'Current Cost', 'With DeltaOps', 'Savings'],
    ['Conservative', data.conservative.current, data.conservative.improved, data.conservative.savings],
    ['Moderate', data.moderate.current, data.moderate.improved, data.moderate.savings],
    ['Full Impact', data.fullImpact.current, data.fullImpact.improved, data.fullImpact.savings]
  ];

  const wsChart = XLSX.utils.aoa_to_sheet(chartData);

  // Styling for chart sheet
  wsChart['!cols'] = [
    { width: 15 },
    { width: 15 },
    { width: 15 },
    { width: 15 }
  ];

  // Add chart sheet
  XLSX.utils.book_append_sheet(wb, wsChart, 'Chart Data');

  // Save file
  XLSX.writeFile(wb, 'deltaops-cost-analysis.xlsx');
};

const ChartComponent = ({ data }) => {
  const chartData = [
    {
      name: 'Conservative',
      current: data.conservative.current,
      improved: data.conservative.improved
    },
    {
      name: 'Moderate',
      current: data.moderate.current,
      improved: data.moderate.improved
    },
    {
      name: 'Full Impact',
      current: data.fullImpact.current,
      improved: data.fullImpact.improved
    }
  ];

  return (
    <div style={{ width: '100%', height: '300px' }}>
      <ResponsiveContainer>
        <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis />
          <Tooltip />
          <Legend />
          <Area 
            type="monotone" 
            name="Current Cost" 
            dataKey="current" 
            stroke="#94a3b8" 
            fill="#f1f5f9" 
          />
          <Area 
            type="monotone" 
            name="With DeltaOps" 
            dataKey="improved" 
            stroke="#3b82f6" 
            fill="#dbeafe" 
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

const exportToPDF = (data) => {
  // Create canvas for chart
  const chartContainer = document.createElement('div');
  chartContainer.style.width = '800px';
  chartContainer.style.height = '400px';
  document.body.appendChild(chartContainer);

  // Render chart to get it in the PDF
  const root = ReactDOM.createRoot(chartContainer);
  root.render(<ChartComponent data={data} />);

  // Allow time for the chart to render
  setTimeout(() => {
    // Create PDF content with chart
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          th, td { padding: 8px; text-align: left; border: 1px solid #ddd; }
          th { background-color: #f8f9fa; }
          .header { font-size: 24px; margin-bottom: 20px; }
          .section { font-size: 18px; margin-top: 20px; margin-bottom: 10px; }
          .benefits { margin-top: 20px; }
          .benefits li { margin: 5px 0; }
          .chart { margin: 20px 0; }
        </style>
      </head>
      <body>
        <div class="header">DeltaOps Cost Analysis Summary</div>
        
        <div class="section">Current Situation</div>
        <table>
          <tr><td>Engineers</td><td>${data.engineerCount}</td></tr>
          <tr><td>Monthly Revenue</td><td>$${data.monthlyRevenue.toLocaleString()}</td></tr>
          <tr><td>Resolution Time (hours)</td><td>${data.currentResolutionTime}</td></tr>
        </table>

        <div class="section">Cost Analysis Chart</div>
        <div class="chart">
          ${chartContainer.innerHTML}
        </div>

        <div class="section">Potential Impact</div>
        <table>
          <tr><td>Annual Savings</td><td>$${Math.round(data.annualSavings).toLocaleString()}</td></tr>
          <tr><td>Time Reduction</td><td>${data.timeReduction}%</td></tr>
          <tr><td>New Resolution Time (hours)</td><td>${data.improvedResolutionTime.toFixed(1)}</td></tr>
        </table>

        <div class="section">Benefits Include</div>
        <ul class="benefits">
          <li>Engineering time cost savings</li>
          <li>Revenue impact reduction</li>
          <li>SLA/customer satisfaction improvements</li>
        </ul>
        
        <div style="margin-top: 30px; font-size: 12px; color: #666;">
          Generated via DeltaOps.app/roi-calculator
        </div>
      </body>
      </html>
    `;

    // Clean up the temporary chart div
    ReactDOM.unmountComponentAtNode(chartContainer);
    document.body.removeChild(chartContainer);

    // Open print dialog
    const printWindow = window.open('', '', 'height=600,width=800');
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.print();
    setTimeout(() => printWindow.close(), 250);
  }, 1000); // Adjust the timeout as needed to ensure the chart renders
};

// ShareModal Component
const ShareModal = ({ 
  isOpen, 
  onClose, 
  data,
  title = "Share Analysis",
  generateSummary,
  CTAText = "Start Free Trial",
  onCTAClick = () => window.location.href = '/pricing'
}) => {
  const [email, setEmail] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(summaryText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };  

  const summaryText = generateSummary ? generateSummary(data) : 
    Object.entries(data).map(([key, value]) => `${key}: ${value}`).join('\n');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {showSuccess && (
            <Alert variant="success">
              <AlertTitle>Analysis Shared Successfully!</AlertTitle>
              <AlertDescription>
                We've sent the analysis to {email}
              </AlertDescription>
            </Alert>
          )}

          {/* Quick Copy Section */}
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-2">Quick Copy</h4>
            <div className="relative">
              <div className="p-4 bg-gray-50 rounded-lg text-sm text-gray-600 whitespace-pre-wrap">
                {summaryText}
              </div>
              <Button
                variant="outline"
                size="sm"
                className="absolute top-2 right-2"
                onClick={handleCopy}
              >
                {copied ? (
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>

          {/* Export Options */}
          <div className="space-y-3">
            <Button 
              variant="outline" 
              className="w-full justify-between"
              onClick={() => exportToPDF(data)}
            >
              <span className="flex items-center">
                <FileText className="h-4 w-4 mr-2" />
                Export as PDF
              </span>
              <Download className="h-4 w-4" />
            </Button>
            
            <Button 
              variant="outline" 
              className="w-full justify-between"
              onClick={() => exportToExcel(data)}
            >
              <span className="flex items-center">
                <FileText className="h-4 w-4 mr-2" />
                Export as Excel
              </span>
              <Download className="h-4 w-4" />
            </Button>
          </div>

          {/* CTA Section */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="flex items-start space-x-3">
              <Share2 className="h-5 w-5 text-blue-600 mt-1 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-medium text-blue-900">Ready to see these improvements?</h4>
                <p className="mt-1 text-sm text-blue-700">
                  Share this analysis with your team and start a conversation about improving your incident response process.
                </p>
                <Button 
                  className="mt-3" 
                  size="sm"
                  onClick={onCTAClick}
                >
                  {CTAText} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ShareButton Component
const ShareButton = ({ 
  data, 
  generateSummary,
  buttonText = "Share Analysis",
  modalTitle,
  CTAText,
  onCTAClick
}) => {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <Button 
        onClick={() => setShowModal(true)}
        variant="outline"
        className="group relative"
      >
        <span className="flex items-center">
          <Share2 className="mr-2 h-4 w-4" />
          {buttonText}
        </span>
      </Button>

      <ShareModal 
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        data={data}
        generateSummary={generateSummary}
        title={modalTitle}
        CTAText={CTAText}
        onCTAClick={onCTAClick}
      />
    </>
  );
};

export { ShareModal, ShareButton };
